import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import type { RecoverySetSummary, DashboardSummary, DashboardStatistics } from '../api'
import { formatDateForLocale } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'

/** Format elapsed time as days, hours and minutes (e.g. "1d 2h 30m"). */
function formatElapsed(isoDate: string): string {
    const d = new Date(isoDate)
    if (isNaN(d.getTime())) return ''
    const now = new Date()
    const diffMs = Math.max(0, now.getTime() - d.getTime())
    const totalMinutes = Math.floor(diffMs / 60_000)
    const days = Math.floor(totalMinutes / (24 * 60))
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
    const minutes = totalMinutes % 60
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)
    return parts.join(' ')
}

/** Bar fill class by percent: 0 = red, 0 < x < 100 = amber, 100 = green */
function progressBarFillClass(percent: number): string {
    if (percent <= 0) return 'bg-red-500'
    if (percent >= 100) return 'bg-green-500'
    return 'bg-amber-500'
}

/** Thin horizontal progress bar: light grey track, filled segment by percent */
function ProgressBar({ percent, fillClassName, className }: { percent: number; fillClassName?: string; className?: string }) {
    const pct = Math.min(100, Math.max(0, percent))
    return (
        <div className={cn('h-1.5 w-full rounded-full bg-muted overflow-hidden', className)} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div
                className={cn('h-full rounded-full transition-all duration-300', fillClassName ?? 'bg-amber-500')}
                style={{ width: `${pct}%` }}
            />
        </div>
    )
}

function formatStartDate(startTime: string | null): string {
    if (!startTime) return '—'
    const d = new Date(startTime)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

type MobilisationCellId =
    | 'usersActivated'
    | 'communications'
    | 'compromised'
    | 'impacted'
    | 'mobilisationTasks'
    | 'technicalChecklist'

/** Fixed order: Activated Users, Messages (Communications), Mobilisation Tasks, Technical Checklist, Number Read (Compromised), Impacted */
function mobilisationCellOrder(
    summary: DashboardSummary | null | undefined,
    stats: DashboardStatistics | null | undefined
): MobilisationCellId[] {
    const order: MobilisationCellId[] = [
        'usersActivated',
        'communications',
        'mobilisationTasks',
        'technicalChecklist',
        'compromised',
        'impacted',
    ]
    return order.filter((id) => {
        if (id === 'usersActivated' || id === 'communications' || id === 'compromised') return summary != null
        if (id === 'impacted' || id === 'mobilisationTasks' || id === 'technicalChecklist') return stats != null
        return true
    })
}

function renderMobilisationCell(
    id: MobilisationCellId,
    summary: DashboardSummary | null | undefined,
    stats: DashboardStatistics | null | undefined,
    t: (key: string, fallback?: string) => string
): ReactNode {
    const baseCellClass = 'min-h-[56px] min-w-0 flex flex-col items-center rounded border border-border p-1.5'
    const labelClass = 'text-[9px] text-muted-foreground text-center mt-0.5'
    switch (id) {
        case 'usersActivated':
            if (summary == null) return null
            return (
                <div key={id} className={baseCellClass}>
                    <span className="text-base font-semibold tabular-nums text-foreground">{summary.percentActivated}%</span>
                    <div className="w-full mt-1">
                        <ProgressBar percent={summary.percentActivated} fillClassName={progressBarFillClass(summary.percentActivated)} />
                    </div>
                    <span className={labelClass}>{t('recoverRequests.usersActivated', 'Users Activated')}</span>
                </div>
            )
        case 'communications':
            if (summary == null) return null
            return (
                <div key={id} className={baseCellClass}>
                    <span className="text-base font-semibold tabular-nums text-foreground">{summary.noMessagesSent}</span>
                    <span className={labelClass}>{t('recoverRequests.communications', 'Comms')}</span>
                </div>
            )
        case 'compromised':
            if (summary == null) return null
            const compromisedHighlight = summary.noRed > 0 ? 'border-red-500 bg-red-500/10 dark:bg-red-500/20' : ''
            return (
                <div key={id} className={cn(baseCellClass, compromisedHighlight)}>
                    <span className="text-base font-semibold tabular-nums text-red-600 dark:text-red-400">{summary.noRed}</span>
                    <span className={labelClass}>{t('recoverRequests.compromisedObjects', 'Compromised')}</span>
                </div>
            )
        case 'impacted':
            if (stats == null) return null
            return (
                <div key={id} className={cn(baseCellClass, 'border-amber-500 bg-amber-500/10 dark:bg-amber-500/20')}>
                    <span className="text-base font-semibold tabular-nums text-amber-700 dark:text-amber-400">{stats.noImpacted}</span>
                    <span className={labelClass}>{t('recoverRequests.impacted', 'Impacted')}</span>
                </div>
            )
        case 'mobilisationTasks':
            if (stats == null) return null
            return (
                <div key={id} className={baseCellClass}>
                    <span className="text-base font-semibold tabular-nums text-foreground">{stats.mobPercentComplete}%</span>
                    <div className="w-full mt-1">
                        <ProgressBar percent={stats.mobPercentComplete} fillClassName={progressBarFillClass(stats.mobPercentComplete)} />
                    </div>
                    <span className={labelClass}>{t('recoverRequests.mobilisationTasks', 'Mob. Tasks')}</span>
                </div>
            )
        case 'technicalChecklist':
            if (stats == null) return null
            return (
                <div key={id} className={baseCellClass}>
                    <span className="text-base font-semibold tabular-nums text-foreground">{stats.taskPercentComplete}%</span>
                    <div className="w-full mt-1">
                        <ProgressBar percent={stats.taskPercentComplete} fillClassName={progressBarFillClass(stats.taskPercentComplete)} />
                    </div>
                    <span className={labelClass}>{t('recoverRequests.technicalChecklist', 'Tech Checklist')}</span>
                </div>
            )
        default:
            return null
    }
}

interface RecoverySetCardProps {
    set: RecoverySetSummary
    onClick: () => void
    /** When true, this card is the one with the active incident (highlighted border) */
    isActiveIncident?: boolean
    /** RecoverySetSummary view data (same for all cards) */
    dashboardSummary?: DashboardSummary | null
    /** RecoverySetStatistics for this set (per Setid) */
    dashboardStats?: DashboardStatistics | null
}

export function RecoverySetCard({ set, onClick, isActiveIncident, dashboardSummary, dashboardStats }: RecoverySetCardProps) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language || 'en-US'
    const isEmpty = set.requestIdCount === 0
    const isActive = set.inProgress > 0 || set.issue > 0 || set.assigned > 0
    const runbookPercent = set.runbookPercentComplete != null ? set.runbookPercentComplete : (set.completionPercent ?? (set.total > 0 ? Math.round((set.completed / set.total) * 100) : 0))
    const completionPercent = Math.min(100, Math.max(0, runbookPercent))
    const mobilisationPercent = set.mobilisationPercent ?? (set.mobilisationTotalSteps > 0 ? Math.round((set.mobilisationCompletedSteps / set.mobilisationTotalSteps) * 100) : 0)
    const tccPercent = set.technicalChecklistPercent ?? 0
    const size = 28
    const stroke = 2.5
    const r = (size - stroke) / 2
    const circumference = 2 * Math.PI * r
    const dash = (completionPercent / 100) * circumference

    return (
        <Card
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
            className={cn(
                'cursor-pointer transition-all hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isEmpty && 'border-border bg-muted/10',
                isActive && !isEmpty && 'shadow-sm',
                isActiveIncident && !isEmpty && 'border-t-4 border-t-blue-500 dark:border-t-blue-400'
            )}
        >
            <CardHeader className="pb-1.5 pt-2 px-3">
                <div className="flex items-start justify-between gap-1.5">
                    <span className={cn('font-semibold truncate text-foreground text-sm')}>
                        {set.name || `Set ${set.id}`}
                    </span>
                    {!isEmpty && (
                        <div className="relative shrink-0" style={{ width: size, height: size }}>
                            <svg width={size} height={size} className="rotate-[-90deg]" aria-hidden>
                                <circle
                                    cx={size / 2}
                                    cy={size / 2}
                                    r={r}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={stroke}
                                    className="text-muted/30"
                                />
                                <circle
                                    cx={size / 2}
                                    cy={size / 2}
                                    r={r}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={stroke}
                                    strokeDasharray={`${dash} ${circumference - dash}`}
                                    strokeLinecap="round"
                                    className="text-primary transition-all duration-300"
                                />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold tabular-nums">
                                {completionPercent}%
                            </span>
                        </div>
                    )}
                </div>
                {set.created && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t('recoverRequests.created', 'Created')}{' '}
                        {formatDateForLocale(set.created, locale, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                )}
                {!isEmpty && set.startTime && (
                    <div className="flex items-center justify-between gap-1.5 mt-0.5 text-[11px] text-muted-foreground">
                        <span>
                            {t('recoverRequests.started', 'Started')} {formatStartDate(set.startTime)}
                        </span>
                        <span className="tabular-nums shrink-0">{formatElapsed(set.startTime)}</span>
                    </div>
                )}
            </CardHeader>
            {isEmpty ? (
                <CardContent className="flex flex-col min-h-[100px] px-3 pb-3 pt-0">
                    <div className="flex-1 flex items-center justify-center py-3">
                        <p className="text-xs text-muted-foreground italic text-center">
                            {t('recoverRequests.noActiveRequestsReady', 'No active requests — ready for assignment')}
                        </p>
                    </div>
                    {(dashboardSummary != null || dashboardStats != null) && (
                        <div className="border-t pt-2 mt-2 space-y-1.5">
                            <div className="text-[10px] font-medium text-muted-foreground">
                                {t('recoverRequests.mobilisation.tabTitle', 'Mobilisation')}
                            </div>
                            <div
                                className="grid gap-1.5"
                                style={{ gridTemplateColumns: `repeat(${mobilisationCellOrder(dashboardSummary ?? null, dashboardStats ?? null).length}, minmax(0, 1fr))` }}
                            >
                                {mobilisationCellOrder(dashboardSummary ?? null, dashboardStats ?? null).map((id) =>
                                    renderMobilisationCell(id, dashboardSummary, dashboardStats, t)
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            ) : (
                <CardContent className="space-y-2 text-sm px-3 pb-3 pt-0">
                    <div className="grid grid-cols-5 gap-1 text-center">
                        <div className="rounded bg-muted/60 px-1 py-0.5 min-w-0">
                            <span className="font-semibold tabular-nums text-foreground text-sm">{set.total}</span>
                            <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.total', 'Total')}</span>
                        </div>
                        <div className="rounded bg-blue-500/15 px-1 py-0.5 min-w-0">
                            <span className="font-semibold tabular-nums text-blue-700 dark:text-blue-300 text-sm">{set.assigned}</span>
                            <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.assigned', 'Assigned')}</span>
                        </div>
                        <div className="rounded bg-amber-500/15 px-1 py-0.5 min-w-0">
                            <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-300 text-sm">{set.inProgress}</span>
                            <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.inProgress', 'In progress')}</span>
                        </div>
                        <div className="rounded bg-red-500/15 px-1 py-0.5 min-w-0">
                            <span className="font-semibold tabular-nums text-red-700 dark:text-red-300 text-sm">{set.issue}</span>
                            <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.issues', 'Issues')}</span>
                        </div>
                        <div className="rounded bg-green-500/15 px-1 py-0.5 min-w-0">
                            <span className="font-semibold tabular-nums text-green-700 dark:text-green-300 text-sm">{set.completed}</span>
                            <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.completed', 'Completed')}</span>
                        </div>
                    </div>
                    {(set.noEngineers != null || set.noAvailable != null) && (
                        <div className="grid grid-cols-2 gap-1 text-center">
                            <div className="rounded bg-muted/60 px-1 py-0.5 min-w-0">
                                <span className="font-semibold tabular-nums text-foreground text-sm">{set.noEngineers ?? 0}</span>
                                <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.noEngineers', 'No Engineers')}</span>
                            </div>
                            <div className="rounded bg-emerald-500/15 px-1 py-0.5 min-w-0">
                                <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300 text-sm">{set.noAvailable ?? 0}</span>
                                <span className="block text-[9px] text-muted-foreground truncate">{t('recoverRequests.availableEngineers', 'Available Engineers')}</span>
                            </div>
                        </div>
                    )}
                    {(dashboardSummary != null || dashboardStats != null) && (
                        <div className="border-t pt-2 mt-2 space-y-1.5">
                            <div className="text-[10px] font-medium text-muted-foreground">
                                {t('recoverRequests.mobilisation.tabTitle', 'Mobilisation')}
                            </div>
                            <div
                                className="grid gap-1.5"
                                style={{ gridTemplateColumns: `repeat(${mobilisationCellOrder(dashboardSummary ?? null, dashboardStats ?? null).length}, minmax(0, 1fr))` }}
                            >
                                {mobilisationCellOrder(dashboardSummary ?? null, dashboardStats ?? null).map((id) =>
                                    renderMobilisationCell(id, dashboardSummary, dashboardStats, t)
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    )
}
